import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Loader2, User, AlertCircle, ArrowLeft } from 'lucide-react';
import { xhrRequest } from '../lib/xhrRequest';
import TipCheckout from '../components/TipCheckout';
import Footer from '../components/Footer';

const PRAISE_TAGS = ["Great service", "Fast", "Friendly", "Above and beyond"];
const PRESET_AMOUNTS = [5, 10, 20];

export default function PublicTipPage() {
  const { publicSlug } = useParams();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [amount, setAmount] = useState(10);
  const [customAmount, setCustomAmount] = useState('');
  const [selectedPraise, setSelectedPraise] = useState('');
  const [note, setNote] = useState('');
  const [creatingIntent, setCreatingIntent] = useState(false);

  // Embedded checkout state. When `clientSecret` is set, we swap the
  // "Send tip" button for the in-page TipCheckout component.
  const [intent, setIntent] = useState(null); // { clientSecret, publishableKey, tipId }

  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

  useEffect(() => {
    let cancelled = false;

    const fetchProfile = async () => {
      try {
        const response = await fetch(`${API}/u/${publicSlug}`);
        if (cancelled) return;
        if (!response.ok) {
          setError(response.status === 404 ? 'User not found' : 'Failed to load profile');
          return;
        }
        const data = await response.json();
        if (!cancelled) setProfile(data);
      } catch (err) {
        if (!cancelled) setError('Failed to load profile');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchProfile();
    return () => { cancelled = true; };
  }, [publicSlug, API]);

  const finalAmount = useMemo(() => {
    const n = Number(customAmount);
    if (customAmount.trim() !== '' && !Number.isNaN(n) && n > 0) return n;
    return amount;
  }, [amount, customAmount]);

  const handleContinue = async () => {
    if (!profile?.stripeOnboarded) {
      toast.error('This user cannot receive tips yet');
      return;
    }
    if (finalAmount < 1) {
      toast.error('Minimum tip is $1');
      return;
    }
    if (finalAmount > 500) {
      toast.error('Maximum tip is $500');
      return;
    }

    setCreatingIntent(true);
    try {
      const { ok, status, data } = await xhrRequest(
        'POST',
        `${API}/tips/create-payment-intent`,
        {
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            publicSlug,
            amount: finalAmount,
            praiseTag: selectedPraise || null,
            note: note || null,
            originUrl: window.location.origin,
          }),
        }
      );

      if (!ok) {
        throw new Error(data.detail || `Failed to start payment (${status})`);
      }
      if (!data.clientSecret || !data.publishableKey) {
        throw new Error('Payment is not available right now. Please try again.');
      }

      setIntent({
        clientSecret: data.clientSecret,
        publishableKey: data.publishableKey,
        tipId: data.tipId,
      });
    } catch (err) {
      toast.error(err.message);
    } finally {
      setCreatingIntent(false);
    }
  };

  const handlePaymentSuccess = (paymentIntentId) => {
    navigate(`/thanks?tip=${intent.tipId}&payment_intent=${paymentIntentId}`);
  };

  const handlePaymentError = (msg) => {
    toast.error(msg);
  };

  const handleBackToAmount = () => {
    // Allow guest to change amount / praise / note before paying.
    setIntent(null);
  };

  if (loading) {
    return (
      <div className="tip-page">
        <div className="spinner" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="tip-page">
        <div className="tip-card text-center">
          <div className="w-16 h-16 rounded-full bg-red-500/10 flex items-center justify-center mx-auto mb-4">
            <AlertCircle size={32} className="text-red-500" />
          </div>
          <h1 className="text-xl font-heading font-bold mb-2">{error}</h1>
          <p className="text-[#A1A1AA]">This tip page doesn't exist or has been removed.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="tip-page" data-testid="public-tip-page">
      <div className="tip-card animate-fade-in">
        {/* Profile Header */}
        <div className="flex items-center gap-4 mb-5">
          <div className="w-16 h-16 rounded-2xl bg-white/10 flex items-center justify-center overflow-hidden">
            {profile?.photoUrl ? (
              <img src={profile.photoUrl} alt="" className="w-full h-full object-cover" />
            ) : (
              <User size={28} className="text-[#52525B]" />
            )}
          </div>
          <div>
            <h1 className="text-xl font-heading font-bold" data-testid="profile-name">
              {profile?.displayName}
            </h1>
            <p className="text-[#A1A1AA]" data-testid="profile-title">
              {profile?.serviceTitle}
            </p>
          </div>
        </div>

        <p className="text-[#A1A1AA] text-sm mb-5">
          Send a tip and leave a quick note of appreciation.
        </p>

        {/* Payouts Not Connected Warning */}
        {!profile?.stripeOnboarded && (
          <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-4 mb-5" data-testid="payouts-not-connected-warning">
            <div className="flex items-start gap-3">
              <AlertCircle size={20} className="text-amber-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-amber-200">Payouts not connected</p>
                <p className="text-sm text-amber-200/70 mt-1">
                  {profile?.displayName} hasn't connected their payout method yet. Check back later!
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Amount + praise + note are only shown BEFORE we've created
            the PaymentIntent. After clicking "Continue" we collapse them
            into a small summary and render the embedded payment UI. */}
        {!intent ? (
          <>
            {/* Amount Selection */}
            <div className="mb-5">
              <p className="text-xs uppercase tracking-widest text-[#52525B] mb-3">Choose an amount</p>
              <div className="flex gap-3 mb-3">
                {PRESET_AMOUNTS.map((v) => (
                  <button
                    key={v}
                    className={`amount-chip ${v === amount && customAmount.trim() === '' ? 'selected' : ''}`}
                    onClick={() => {
                      setAmount(v);
                      setCustomAmount('');
                    }}
                    data-testid={`amount-chip-${v}`}
                  >
                    ${v}
                  </button>
                ))}
              </div>
              <input
                type="text"
                value={customAmount}
                onChange={(e) => setCustomAmount(e.target.value.replace(/[^\d.]/g, ''))}
                placeholder="Custom amount"
                inputMode="decimal"
                className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder:text-[#52525B] focus:border-[#F5C542] focus:outline-none transition-colors"
                data-testid="custom-amount-input"
              />
            </div>

            {/* Praise Tags */}
            <div className="mb-4">
              <p className="text-xs uppercase tracking-widest text-[#52525B] mb-3">Quick praise (optional)</p>
              <div className="flex flex-wrap gap-2">
                {PRAISE_TAGS.map((tag) => (
                  <button
                    key={tag}
                    className={`praise-tag ${selectedPraise === tag ? 'selected' : ''}`}
                    onClick={() => setSelectedPraise(selectedPraise === tag ? '' : tag)}
                    data-testid={`praise-tag-${tag.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {tag}
                  </button>
                ))}
              </div>
            </div>

            {/* Note */}
            <div className="mb-5">
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Add a personal note (optional)"
                rows={2}
                maxLength={200}
                className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder:text-[#52525B] focus:border-[#F5C542] focus:outline-none resize-none transition-colors"
                data-testid="note-input"
              />
            </div>

            <button
              disabled={!profile?.stripeOnboarded || creatingIntent || finalAmount <= 0}
              onClick={handleContinue}
              className="btn-primary flex items-center justify-center gap-2"
              data-testid="pay-button"
            >
              {creatingIntent ? (
                <>
                  <Loader2 className="animate-spin" size={20} />
                  Preparing payment...
                </>
              ) : (
                `Continue with $${finalAmount.toFixed(2)}`
              )}
            </button>
          </>
        ) : (
          <>
            {/* Compact summary + change-amount link */}
            <div
              className="bg-white/5 rounded-xl px-4 py-3 mb-5 flex items-center justify-between"
              data-testid="tip-summary"
            >
              <div>
                <p className="text-xs uppercase tracking-widest text-[#52525B]">Tipping</p>
                <p className="font-heading font-bold text-lg text-[#F5C542]">
                  ${finalAmount.toFixed(2)}
                  {selectedPraise && (
                    <span className="ml-2 text-xs font-normal text-[#A1A1AA]">
                      · {selectedPraise}
                    </span>
                  )}
                </p>
              </div>
              <button
                onClick={handleBackToAmount}
                className="text-xs text-[#A1A1AA] hover:text-white flex items-center gap-1"
                data-testid="change-amount-button"
              >
                <ArrowLeft size={14} />
                Change
              </button>
            </div>

            <TipCheckout
              clientSecret={intent.clientSecret}
              publishableKey={intent.publishableKey}
              amount={finalAmount}
              recipientName={profile?.displayName}
              onSuccess={handlePaymentSuccess}
              onError={handlePaymentError}
            />
          </>
        )}

        <p className="text-center text-xs text-[#52525B] mt-4">
          Tips are optional. Payments processed securely by Stripe.
        </p>
      </div>
      <Footer className="mt-8" />
    </div>
  );
}
